import { StandardEvents } from '@shopify/events';

/**
 * Price + price per glass (blocks/price-per-glass.liquid).
 * Follows the selected variant and the purchase option: the Appstle widget puts `selling_plan` radios in the
 * product form (plan id = Subscribe & Save, empty value = one-time). Prices come from the block's JSON
 * (variant prices and selling plan allocation prices), so nothing is hardcoded.
 */
class PricePerGlass extends HTMLElement {
  /** @type {any} */ data = null;
  /** @type {Element|Document} */ #scope = document;
  /** @type {string|null} */ #variantId = null;
  /** @type {MutationObserver|null} */ #observer = null;
  #queued = false;

  connectedCallback() {
    const json = this.querySelector('script[type="application/json"]');
    if (!json) return;
    this.data = JSON.parse(json.textContent || '{}');
    this.#scope = this.closest('.shopify-section, dialog') ?? document;
    this.#scope.addEventListener('change', this.#onChange);
    this.#scope.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    // Appstle renders its widget (with its default choice) after load, without a change event.
    const form = this.#form();
    if (form) {
      this.#observer = new MutationObserver(() => this.#queue());
      this.#observer.observe(form, { childList: true, subtree: true });
    }
    this.update();
  }

  disconnectedCallback() {
    this.#scope.removeEventListener('change', this.#onChange);
    this.#scope.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#observer?.disconnect();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) this.#queue();
  };

  /** @param {any} event */
  #onProductSelect = (event) => {
    event.promise
      ?.then((/** @type {any} */ result) => {
        const id = result?.detail?.resource?.id;
        if (id) this.#variantId = String(id);
        this.#queue();
      })
      .catch(() => {});
  };

  #queue() {
    if (this.#queued) return;
    this.#queued = true;
    requestAnimationFrame(() => {
      this.#queued = false;
      this.update();
    });
  }

  /** @returns {HTMLFormElement|null} */
  #form() {
    const idInput = this.#scope.querySelector('form input[name="id"]');
    return idInput instanceof HTMLInputElement ? idInput.form : null;
  }

  /** Selected selling plan: plan id, '' for one-time, or null while the Appstle widget hasn't rendered. */
  #sellingPlan() {
    const form = this.#form();
    if (!form) return null;
    const radios = form.querySelectorAll('input[name="selling_plan"]');
    if (radios.length) {
      const checked = form.querySelector('input[name="selling_plan"]:checked');
      return checked instanceof HTMLInputElement ? checked.value : '';
    }
    const field = form.querySelector('[name="selling_plan"]');
    return field instanceof HTMLInputElement || field instanceof HTMLSelectElement ? field.value : null;
  }

  update() {
    const { variants, servings, textSubscription, textOneTime } = this.data;
    // The variant from the latest variant picker event wins; the form's id input can update a moment later.
    const idInput = this.#form()?.querySelector('input[name="id"]');
    const id = this.#variantId || (idInput instanceof HTMLInputElement && idInput.value) || this.data.selected;
    const variant = variants[id] ?? variants[this.data.selected];
    if (!variant) return;

    const planPrices = Object.values(variant.plans);
    const plan = this.#sellingPlan();
    let subscription = null;
    if (planPrices.length && plan !== '') {
      subscription = plan && variant.plans[plan] != null ? variant.plans[plan] : Math.min(...planPrices);
    }

    const price = subscription ?? variant.price;
    const compare = subscription != null ? variant.price : variant.compare;
    const priceEl = this.querySelector('[data-ppg-price]');
    const compareEl = this.querySelector('[data-ppg-compare]');
    if (priceEl) priceEl.textContent = this.#money(price);
    if (compareEl) {
      const show = compare != null && compare > price;
      compareEl.textContent = show ? this.#money(compare) : '';
      compareEl.closest('[data-ppg-compare-group]')?.toggleAttribute('hidden', !show);
    }

    const text = subscription != null ? textSubscription : textOneTime;
    const textEl = this.querySelector('[data-ppg-text]');
    if (textEl) {
      textEl.textContent = text ? text.replace('[price]', this.#money(Math.round(price / servings))) : '';
      textEl.toggleAttribute('hidden', !text);
    }
  }

  /** @param {number} cents */
  #money(cents) {
    const value = cents / 100;
    /** @param {number} decimals @param {string} [thousands] @param {string} [decimal] */
    const number = (decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = value.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (decimals ? decimal + fraction : '');
    };
    /** @type {Record<string, string>} */
    const amounts = {
      amount: number(2),
      amount_no_decimals: number(0),
      amount_with_comma_separator: number(2, '.', ','),
      amount_no_decimals_with_comma_separator: number(0, '.', ','),
      amount_with_apostrophe_separator: number(2, "'", '.'),
    };
    return this.data.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (/** @type {string} */ _, key) => amounts[key] ?? amounts.amount);
  }
}

if (!customElements.get('price-per-glass')) customElements.define('price-per-glass', PricePerGlass);
